import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const BSCNursingCoursePage: React.FC = () => {
  const bscNursingData = {
    title: "Bachelor of Science in Nursing",
    shortName: "B.Sc Nursing",
    duration: "4 Years",
    description: "A comprehensive healthcare program that prepares students to become professional registered nurses capable of providing quality patient care, health promotion, and disease prevention.",
    overview: "B.Sc Nursing is a four-year undergraduate degree program that combines theoretical knowledge with extensive practical training in healthcare settings. The program covers human anatomy, physiology, pharmacology, psychology, nutrition, and various nursing specializations. Students learn to assess patient conditions, administer medications, assist in medical procedures, and provide emotional support to patients and families. The curriculum emphasizes evidence-based practice, critical thinking, and ethical decision-making in healthcare. Graduates are equipped to work in hospitals, clinics, community health centers, schools, and various healthcare organizations as professional nurses.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, Biology, and English",
      "Minimum 45% aggregate marks (40% for SC/ST)",
      "Age: 17-35 years (varies by state)",
      "Medical fitness certificate required",
      "Some states require entrance exam qualification",
      "Good physical and mental health",
      "English proficiency essential"
    ],
    careerProspects: {
      roles: [
        "Staff Nurse",
        "Head Nurse",
        "Nursing Supervisor",
        "ICU Nurse",
        "Operation Theatre Nurse",
        "Community Health Nurse",
        "School Health Nurse",
        "Nursing Educator",
        "Nursing Administrator",
        "Clinical Research Coordinator",
        "Home Care Nurse",
        "Psychiatric Nurse"
      ],
      industries: [
        "Government Hospitals",
        "Private Healthcare",
        "Community Health Centers",
        "Nursing Homes",
        "Schools and Colleges",
        "Corporate Health Centers",
        "Home Healthcare",
        "Mental Health Facilities",
        "Rehabilitation Centers",
        "Medical Research",
        "Healthcare NGOs",
        "International Healthcare"
      ],
      averageSalary: "₹3-12 LPA (varies by experience and location)",
      topRecruiters: [
        "AIIMS",
        "Apollo Hospitals",
        "Fortis Healthcare",
        "Max Healthcare",
        "Manipal Hospitals",
        "Government Health Services",
        "Armed Forces Medical Services",
        "WHO",
        "Narayana Health",
        "Medanta",
        "International Hospitals",
        "Home Healthcare Companies"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Anatomy and Physiology",
          "Fundamentals of Nursing",
          "Psychology",
          "Sociology",
          "English and Communication",
          "Computer Applications",
          "Nutrition and Dietetics",
          "First Aid and Emergency Care"
        ]
      },
      {
        year: 2,
        subjects: [
          "Pathology and Microbiology",
          "Pharmacology",
          "Medical Surgical Nursing - I",
          "Community Health Nursing - I",
          "Environmental Studies",
          "Health Education",
          "Nursing Research and Statistics",
          "Clinical Postings"
        ]
      },
      {
        year: 3,
        subjects: [
          "Medical Surgical Nursing - II",
          "Child Health Nursing",
          "Mental Health Nursing",
          "Obstetrics and Gynecological Nursing",
          "Community Health Nursing - II",
          "Nursing Administration",
          "Clinical Specializations",
          "Intensive Clinical Training"
        ]
      },
      {
        year: 4,
        subjects: [
          "Nursing Leadership and Management",
          "Nursing Education",
          "Research Project",
          "Comprehensive Clinical Rotations",
          "Critical Care Nursing",
          "Geriatric Nursing",
          "Palliative Care",
          "Professional Development"
        ]
      }
    ],
    admissionProcess: [
      "Check eligibility criteria for desired colleges",
      "Appear for state/national entrance exams (if required)",
      "Fill application forms for preferred colleges",
      "Attend counseling sessions",
      "Document verification and medical check-up",
      "Seat allotment based on merit/entrance scores",
      "Fee payment and admission confirmation",
      "Join the allotted nursing college"
    ],
    topColleges: [
      {
        name: "AIIMS College of Nursing",
        location: "New Delhi",
        fees: "₹15,000 PA",
        ranking: "1st in India"
      },
      {
        name: "CMC College of Nursing",
        location: "Vellore, Tamil Nadu",
        fees: "₹2.5 LPA",
        ranking: "2nd in India"
      },
      {
        name: "JIPMER College of Nursing",
        location: "Puducherry",
        fees: "₹8,000 PA",
        ranking: "3rd in India"
      },
      {
        name: "PGIMER College of Nursing",
        location: "Chandigarh",
        fees: "₹25,000 PA",
        ranking: "4th in India"
      },
      {
        name: "BHU College of Nursing",
        location: "Varanasi",
        fees: "₹35,000 PA",
        ranking: "5th in India"
      },
      {
        name: "Tamil Nadu Dr. MGR Medical University",
        location: "Chennai, Tamil Nadu",
        fees: "₹30,000 PA",
        ranking: "Top in Tamil Nadu"
      },
      {
        name: "Government College of Nursing",
        location: "Various States",
        fees: "₹15,000-50,000 PA",
        ranking: "State-wise Rankings"
      }
    ],
    skills: [
      "Patient Care",
      "Clinical Assessment",
      "Medication Administration",
      "Critical Thinking",
      "Communication Skills",
      "Empathy and Compassion",
      "Time Management",
      "Infection Control",
      "Emergency Response",
      "Health Education",
      "Documentation",
      "Teamwork",
      "Ethical Decision Making"
    ],
    futureScope: "The nursing profession has excellent future prospects with growing healthcare needs of an aging population, advances in medical technology, and increasing focus on preventive healthcare. Opportunities exist in specialized areas like critical care, oncology, pediatrics, mental health, and community health. With additional qualifications, nurses can become nurse practitioners, nursing educators, healthcare administrators, or pursue research careers. International opportunities are abundant, especially in developed countries facing nursing shortages. The profession also offers flexibility with options for part-time work, home healthcare, and telehealth services.",
    faqs: [
      {
        question: "What is the difference between GNM and B.Sc Nursing?",
        answer: "GNM (General Nursing and Midwifery) is a 3-year diploma program, while B.Sc Nursing is a 4-year degree program. B.Sc Nursing offers better career prospects, higher salary potential, and opportunities for further education like M.Sc Nursing."
      },
      {
        question: "Is NEET required for B.Sc Nursing admission?",
        answer: "NEET is not mandatory for most B.Sc Nursing programs. However, some states and institutions may have their own entrance exams. It's best to check specific college requirements."
      },
      {
        question: "Can male students pursue B.Sc Nursing?",
        answer: "Absolutely! Nursing is open to all genders. Male nurses are in high demand, especially in critical care units, emergency departments, and mental health facilities."
      },
      {
        question: "What are the job opportunities abroad for B.Sc Nursing graduates?",
        answer: "Excellent opportunities exist in countries like USA, UK, Canada, Australia, Middle East, and European countries. You may need to clear licensing exams like NCLEX-RN (USA), IELTS for English proficiency, and meet specific country requirements."
      },
      {
        question: "Can I specialize in a particular area of nursing?",
        answer: "Yes, after B.Sc Nursing, you can pursue M.Sc Nursing with specializations in areas like Critical Care, Pediatric Nursing, Psychiatric Nursing, Community Health Nursing, Obstetrics & Gynecology, and Medical-Surgical Nursing."
      },
      {
        question: "Is nursing a financially rewarding career?",
        answer: "Yes, nursing offers good financial stability with starting salaries of ₹3-6 LPA, which can grow to ₹8-15 LPA with experience. International opportunities offer even higher compensation packages."
      }
    ]
  };

  return (
    <div className={`min-h-screen font-[Inter,Poppins,sans-serif] antialiased bg-gradient-to-br from-[#0a0a1f] via-[#14142b] to-[#1e1e2f] text-white`} style={{ transition: 'background 0.5s, color 0.5s' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight glow-text drop-shadow-glow text-center">
          {bscNursingData.title}
        </h1>
        <CourseTemplate courseData={bscNursingData} />
      </div>
    </div>
  );
};

export default BSCNursingCoursePage;
